import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
    OrderStatus,
    OrderStatusLabels,
} from '../../../modules/application/orders/shared/constants/order-status';
import { getEnumValues } from '../../functions/enum-helper';
import { FilterOption } from '../../models/grid/filter-option';
import { ReferenceData } from '../resources/models/reference-data';
import { ResourcesService } from '../resources/resources.service';
import {
    WorkflowState,
    WorkflowStateLabels,
} from '../../../modules/service-fulfillment/constants/workflow-state';
import { AdvisorUser } from '../user/models/advisor-user';
import { AdvisorConstants } from '../../../modules/service-fulfillment/constants/advisor-constants';
import { ApplicationFilingStatusEnum } from '../../../modules/application/shared/constants/application-filing-status';
import { PscaFilterStatuses } from '../../../modules/application/shared/constants/psca-status';
import { FccFilterStatuses } from '../../../modules/application/shared/constants/fcc-status';

@Injectable({
    providedIn: 'root',
})
/**
 * A service responsible for retrieving filter options for the grid from the API.
 */
export class GridFilterDataService {
    constructor(private readonly resourcesService: ResourcesService) {}

    getApplicationPurposeFilterOptions(): Observable<FilterOption[]> {
        return this.resourcesService.getApplicationPurposes().pipe(
            map((data: ReferenceData[]) => {
                return data.map((item: ReferenceData) => {
                    return {
                        label: item.description,
                        value: item.code,
                        selected: false,
                    };
                });
            }),
        );
    }

    getApplicationStatusFilterOptions(): Observable<FilterOption[]> {
        return this.resourcesService.getApplicationStatuses().pipe(
            map((data: ReferenceData[]) => {
                return data.map((item: ReferenceData) => {
                    return {
                        label: item.description,
                        value: item.code,
                        selected: false,
                    };
                });
            }),
        );
    }

    getServiceFulfillmentApplicationStatusFilterOptions(): FilterOption[] {
        return (
            [
                ...PscaFilterStatuses,
                ...FccFilterStatuses,
                ApplicationFilingStatusEnum.holdPost5DayHoldRevalidation,
                ApplicationFilingStatusEnum.holdDayX,
                ApplicationFilingStatusEnum.holdWaitOnPublicNoticeDate,
                ApplicationFilingStatusEnum.autoCoordSubmissionFailed,
                ApplicationFilingStatusEnum.coordinationFailed,
                ApplicationFilingStatusEnum.coordinationProcessing,
                ApplicationFilingStatusEnum.coordinationResultsReady,
                ApplicationFilingStatusEnum.coordinationSubmitted,
                ApplicationFilingStatusEnum.ebfSubmissionFailed,
                ApplicationFilingStatusEnum.ebfSubmitted,
                ApplicationFilingStatusEnum.entryComplete,
                ApplicationFilingStatusEnum.entryIncomplete,
                ApplicationFilingStatusEnum.failedTransmittalToFcc,
                ApplicationFilingStatusEnum.fccAwaitingResponse,
                ApplicationFilingStatusEnum.fccReceived,
                ApplicationFilingStatusEnum.limitationCheckFailed,
                ApplicationFilingStatusEnum.pendingTransmissionToFcc,
                ApplicationFilingStatusEnum.submittedToEwaForCertification,
                ApplicationFilingStatusEnum.submittedToFcc,
                ApplicationFilingStatusEnum.submittedToPsca,
            ]
                // Sort the statuses alphabetically, ignoring case
                .sort(
                    new Intl.Collator(undefined, { sensitivity: 'base' })
                        .compare,
                )
                .map((item) => {
                    return {
                        label: item,
                        value: item,
                        selected: false,
                    };
                })
        );
    }

    getOrderStatusFilterOptions(): FilterOption[] {
        return getEnumValues(OrderStatus).map((status) => {
            return {
                label: OrderStatusLabels[status],
                value: OrderStatusLabels[status],
                selected: false,
            };
        });
    }

    getWorkflowStateFilterOptions(): FilterOption[] {
        return getEnumValues(WorkflowState).map((state) => {
            return {
                label: WorkflowStateLabels[state],
                value: WorkflowStateLabels[state],
                selected: false,
            };
        });
    }

    getHandedOverFilterOptions(): FilterOption[] {
        return [
            { label: 'Yes', value: 'Yes', selected: false },
            { label: 'No', value: 'No', selected: false },
        ];
    }

    getAdvisorFilterOptions(): Observable<FilterOption[]> {
        return this.resourcesService.getAdvisors().pipe(
            map((data: AdvisorUser[]) => {
                return [
                    {
                        label: AdvisorConstants.NoneAssigned,
                        value: '',
                        selected: false,
                    },
                    ...data.map((item: AdvisorUser) => {
                        return {
                            label:
                                item.advisorName ??
                                AdvisorConstants.UnnamedAdvisor,
                            value: item.userId.toString(),
                            selected: false,
                        };
                    }),
                ];
            }),
        );
    }
}
